import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_API_BASE_PATH, LUPP_QUERY_KEYS } from '../constants.lupp';
import type { TransformedCheckoutDetail } from '../inventory/transformers.checkout';
import { useStore } from '../../../../store/store';
import { getCountryCodeFromLocale } from '../../../../utils/tools';
import {
  QuotesListResponse,
  GetQuotesListRequest,
  UseGetQuotesParams,
  UseGetQuoteByIdParams,
  UpdateQuoteRequest,
  UseUpdateQuoteParams,
  CreateQuoteRequest,
  UseCreateQuoteParams,
  SendDocusignRequest,
  SendDocusignResponse,
  UseSendDocusignParams,
} from './dto.quotes';
import type {
  FinopsErrorResponse,
  FinopsResponse,
  GetLendersData,
  GetLendersRequest,
  UseGetLendersParams,
} from './dto.quotes';
import { transformQuoteToCheckoutDetail, type QuoteDetailsResponse } from './transformers.quotes';

/*
|--------------------------------------------------------------------------
| GET QUOTES
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch paginated quotes list for a prospect
 * @param params - Prospect ID and query parameters for quotes list
 * @returns Query result with quotes data
 * @example
 * const { data, isLoading, error } = useGetQuotes({
 *   prospectId: 'd3a1f7b2-8e4c-44aa-9c77-1b2c3d4e5f66',
 *   page: 0,
 *   size: 25
 * });
 */

export const useGetQuotes = ({
  page = 0,
  size = 10,
  searchString,
  sortField,
  sortOrder = 'desc',
  enabled = true,
  prospectId,
}: UseGetQuotesParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.QUOTES, prospectId, page, size, searchString, sortField, sortOrder],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        size: String(size),
      });

      const body: GetQuotesListRequest = {};
      if (searchString) body.searchString = searchString;
      if (sortField) body.sortField = sortField;
      if (sortOrder) body.sortOrder = sortOrder;

      const response = await LuppApi.post<GetQuotesListRequest, QuotesListResponse>(
        `${LUPP_API_BASE_PATH}/orders/prospects/${prospectId}/quotes/searches?${queryParams.toString()}`,
        body,
        {
          extractData: (response) => response,
        }
      );
      return {
        items: response.data,
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        currentPageCount: response.currentPageCount,
        totalPages: response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled: enabled && !!prospectId,
  });
};

/*
|--------------------------------------------------------------------------
| GET QUOTE BY ID
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch a single quote by ID
 * @param params - Quote ID and enabled flag
 * @returns Query result with transformed checkout detail data
 * @example
 * const { data, isLoading, error } = useGetQuoteById({
 *   prospectId: 'd3a1f7b2-8e4c-44aa-9c77-1b2c3d4e5f66',
 *   quoteId: 'L2135674613'
 * });
 */

export const useGetQuoteById = ({ prospectId, quoteId, enabled = true }: UseGetQuoteByIdParams) => {
  return useQuery<TransformedCheckoutDetail>({
    queryKey: [LUPP_QUERY_KEYS.QUOTES, quoteId],
    queryFn: async () => {
      // TODO: Uncomment when API is ready
      const response = await LuppApi.get<QuoteDetailsResponse>(
        `${LUPP_API_BASE_PATH}/orders/prospects/${prospectId}/quotes/${quoteId}`
      );
      return transformQuoteToCheckoutDetail(response);
    },
    enabled: enabled && !!quoteId && !!prospectId,
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE QUOTE
|--------------------------------------------------------------------------
*/

/**
 * Hook to update an existing quote
 * @param params - Prospect ID and Quote ID
 * @returns Mutation result for updating a quote
 * @example
 * const { mutate, isPending } = useUpdateQuote({ prospectId: 'P123', quoteId: 'Q456' });
 * mutate({ preferredPaymentMethod: 'Cash', ... });
 */
export const useUpdateQuote = ({ prospectId, quoteId }: UseUpdateQuoteParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateQuoteRequest) => {
      const response = await LuppApi.put<UpdateQuoteRequest, QuoteDetailsResponse>(
        `${LUPP_API_BASE_PATH}/orders/prospects/${prospectId}/quotes/${quoteId}`,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES, quoteId] });
    },
    onError: (error) => {
      console.error('Failed to update quote:', error);
    },
  });
};

/*
|--------------------------------------------------------------------------
| CREATE QUOTE
|--------------------------------------------------------------------------
*/

/**
 * Hook to create a new quote for a prospect
 * @param params - Prospect ID
 * @returns Mutation result for creating a quote
 * @example
 * const { mutate, isPending } = useCreateQuote({ prospectId: 'P123' });
 * mutate({ model: 'Air', vin: '...', preferredPaymentMethod: 'Cash' });
 */
export const useCreateQuote = ({ prospectId }: UseCreateQuoteParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateQuoteRequest) => {
      const response = await LuppApi.post<CreateQuoteRequest, QuoteDetailsResponse>(
        `${LUPP_API_BASE_PATH}/orders/prospects/${prospectId}/quotes`,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES] });
    },
    onError: (error) => {
      console.error('Failed to create quote:', error);
    },
  });
};

/*
|--------------------------------------------------------------------------
| DOWNLOAD QUOTE
|--------------------------------------------------------------------------
*/

/**
 * Parameters for useDownloadQuote hook
 */
export type UseDownloadQuoteParams = {
  prospectId: string;
  quoteId: string;
  language?: string;
  enabled?: boolean;
};

/**
 * Hook to download a quote PDF
 * @param params - Prospect ID and Quote ID
 * @returns Query result with download data
 * @example
 * const { data, isLoading } = useDownloadQuote({ prospectId: 'P123', quoteId: 'Q456' });
 */
export const useDownloadQuote = ({
  prospectId,
  quoteId,
  language = 'en',
  enabled = true,
}: UseDownloadQuoteParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.QUOTES, prospectId, quoteId, 'download', language],
    queryFn: async () => {
      const response = await LuppApi.instance.get<Blob>(
        `${LUPP_API_BASE_PATH}/orders/prospects/${prospectId}/quotes/${quoteId}/download`,
        { responseType: 'blob', headers: { 'x-language': language } }
      );

      // Extract filename from Content-Disposition header
      // Note: Backend must expose this header via CORS for it to be accessible
      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      const filename =
        contentDisposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i)?.[1]?.trim() ||
        `quote-${quoteId}.pdf`;

      return { blob: response.data, filename: decodeURIComponent(filename) };
    },
    enabled: Boolean(prospectId && quoteId && enabled),
  });
};

/*
|--------------------------------------------------------------------------
| SEND DOCUSIGN
|--------------------------------------------------------------------------
*/

/**
 * Hook to send Docusign for a quote
 * @param params - Prospect ID and Quote ID
 * @returns Mutation result for sending Docusign
 * @example
 * const { mutate, isPending } = useSendDocusign({ prospectId: 'P123', quoteId: 'Q456' });
 * mutate({ firstName: 'John', lastName: 'Doe', email: 'john@example.com' });
 */
export const useSendDocusign = ({ prospectId, quoteId }: UseSendDocusignParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SendDocusignRequest) => {
      const response = await LuppApi.post<SendDocusignRequest, SendDocusignResponse>(
        `${LUPP_API_BASE_PATH}/orders/prospects/${prospectId}/quotes/${quoteId}/send-docusign`,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.QUOTES, quoteId] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| GET LENDERS (FinOps)
|--------------------------------------------------------------------------
*/

const FINOPS_BASE_URL = import.meta.env.VITE_FINOPS_BASE_URL;
const FINOPS_API_BASE_PATH = 'finops/finops-eu/v1';

/**
 * Builds a readable message from the FinOps error envelope.
 */
const extractFinopsError = (response: FinopsErrorResponse | undefined): string | null => {
  const fieldErrors =
    response?.errors && response.errors.length > 0
      ? response.errors.map((error) => `${error.field}: ${error.message}`).join(', ')
      : null;
  const message = response?.message?.trim() || null;

  if (fieldErrors && message) return `${message}: ${fieldErrors}`;

  return fieldErrors || message;
};

/**
 * Hook to fetch the FinOps lenders (finance providers) for a market and finance type
 * @param params - Finance type, optional country code and enabled flag
 * @returns Query result with the lenders
 * @example
 * const { data: lenders } = useGetLenders({ financeType: 'Lease' });
 */
export const useGetLenders = ({
  financeType,
  customerType,
  countryCode,
  enabled = true,
}: UseGetLendersParams) => {
  const locale = useStore((state) => state.locale);
  const market = countryCode ?? getCountryCodeFromLocale(locale);

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.LENDERS, market, financeType, customerType],
    queryFn: async (): Promise<GetLendersData['results']> => {
      const requestBody: GetLendersRequest = { countryCode: market, financeType, customerType };

      const response = await fetch(`${FINOPS_BASE_URL}/${FINOPS_API_BASE_PATH}/${market}/lenders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const payload = await response.json().catch(() => undefined);

      if (!response.ok) {
        const message = extractFinopsError(payload as FinopsErrorResponse);
        throw new Error(message ?? `FinOps API error: ${response.status} ${response.statusText}`);
      }

      return (payload as FinopsResponse<GetLendersData>).data?.results ?? [];
    },
    enabled: enabled && Boolean(financeType),
  });
};
